import classNames from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import { numberOfEmptyRows } from "../../AppConstants";
import i18n from "../../i18n";
import { TextFilter } from "../../Analytics/components/Filters/TextFilter";
import { NumberFilter } from "../../Analytics/components/Filters/NumberFilter";
import { SelectFilter } from "../../Analytics/components/Filters/SelectFilter";
import { DateFilter } from "../../Analytics/components/Filters/DateFilter";
import { EmptyListModal } from "../../Shared/EmptyListModal/EmptyListModal";
import { SkeletonTableRow } from "../../Shared/SkeletonTableRow";
import { Tooltip } from "react-tooltip";
import { UpdateListItem } from "./UpdateListItem";
import { IStoreState } from "../../Model";
import { setFilter, clearFilter, clearAllFilters } from "../duck/Actions";
import { IFilter } from "../duck/Models";
import "../Update.scss";

export interface IUpdatesProps {
  updates: Api.IPagedResult<Api.INotificationViewModel>;
  onSortChange?: (orderBy: string) => void;
  orderBy?: string;
  filters: IFilter[];
  filteredResults: Api.INotificationViewModel[];
  setFilter: (field: string, value: any) => void;
  clearFilter: (field: string) => void;
  clearAllFilters: () => void;
}


class UpdateList extends React.Component<IUpdatesProps> {
  // Column definitions used for reference in filter components

  constructor(props: IUpdatesProps) {
    super(props);

    this.handleClickHeader = this.handleClickHeader.bind(this);
    this.handleFilterApply = this.handleFilterApply.bind(this);
    this.handleFilterClear = this.handleFilterClear.bind(this);
    this.getCategoryOptions = this.getCategoryOptions.bind(this);
  }

  // Helper function to get filter value from array (same pattern as Analytics)
  private getFilterValue(field: string, operator?: string): any {
    const filter = this.props.filters.find((f: IFilter) =>
      f.field === field && (operator ? f.operator === operator : true)
    );
    return filter?.value;
  }

  // Helper function to check if any filter is active for a field
  private isFilterActive(field: string): boolean {
    return this.props.filters.some((f: IFilter) => f.field === field);
  }

  private handleFilterApply(field: string, value: any, operator: string = "eq", hasSecondFilter: boolean = false) {
    this.props.setFilter(field, value);
  }

  private handleFilterClear(field: string) {
    this.props.clearFilter(field);
  }

  private getCategoryOptions() {
    // Extract unique categories from the data
    const categories = Array.from(new Set(
      this.props.updates.results.map(item => item.responseCategory)
    ));
    
    // Return a Promise that resolves with the categories data
    return Promise.resolve({ facets: [{ groups: categories.map(cat => ({ key: cat })) }] });
  }


  public render() {
    const isSkeleton: boolean = this.props.updates.results && this.props.updates.rowCount === 0;
    const displayResults = this.props.filteredResults.length > 0 ? this.props.filteredResults : this.props.updates.results;
    // Check if the current sorting is on docUpdateDays (default sorting)
    const isDefaultSorting = !this.props.orderBy || this.props.orderBy.startsWith('docUpdateDays');
    
    return (
      <div className="updates-container">
        <div className="updates">
          <h1 className="title">{i18n.t("home.updates")}</h1>
          {this.props.updates.rowCount > 0 && <h5 className="message">{i18n.t("home.updateExistsMessage")}</h5>}
          {!this.props.updates.rowCount && <h5 className="message">{i18n.t("home.noUpdatesMessage")}</h5>}
          {/* Filter actions removed as per requirement */}
          
          
          <div className="vertical-spacer"></div>
          <div className="update-list">
            <table className={classNames("table", {"table-hover":!isSkeleton}, "table-responsive", {"default-sorting": isDefaultSorting})} id="updates-table">
              <thead>
                <tr>
                  <th className={classNames("referenceId", { "filter-applied": this.isFilterActive('referenceId') })} scope="col" data-column-index="0" data-property="referenceId">
                    <span className="updates-table-heading">
                      {i18n.t("update.titleIdHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <NumberFilter
                        field={{ name: 'ID', property: 'referenceId' }}
                        firstFilterValue={this.getFilterValue('referenceId', 'ge')}
                        secondFilterValue={this.getFilterValue('referenceId', 'le')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                      />
                    </span>
                  </th>
                  <th className={classNames("title", { "filter-applied": this.isFilterActive('responseTitle') })} scope="col" data-column-index="1" data-property="responseTitle">
                    <span className="updates-table-heading">
                      {i18n.t("update.titleColHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <TextFilter
                        field={{ name: 'Response Title', property: 'responseTitle' }}
                        value={this.getFilterValue('responseTitle')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                      />
                    </span>
                  </th>
                  <th className={classNames("category", { "filter-applied": this.isFilterActive('responseCategory') })} scope="col" data-column-index="2" data-property="responseCategory">
                    <span className="updates-table-heading">
                      {i18n.t("update.categoryColHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <SelectFilter
                        field={{ name: 'Category', property: 'responseCategory', multiSelection: true }}
                        value={this.getFilterValue('responseCategory')}
                        asyncloader={this.getCategoryOptions}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                      />
                    </span>
                  </th>
                  <th className={classNames("lastCuratedDate", { "filter-applied": this.isFilterActive('lastUpdatedArtefactDate') })} scope="col" data-column-index="3" data-property="lastUpdatedArtefactDate">
                    <span className="updates-table-heading">
                      {i18n.t("update.lastResponseUpdatedHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <DateFilter
                        field={{ name: 'Last Response Updated', property: 'lastUpdatedArtefactDate' }}
                        firstFilterValue={this.getFilterValue('lastUpdatedArtefactDate', 'ge')}
                        secondFilterValue={this.getFilterValue('lastUpdatedArtefactDate', 'le')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                        showTime={false}
                      />
                    </span>
                  </th>
                  <th className={classNames("date", { "filter-applied": this.isFilterActive('dateCreated') })} scope="col" data-column-index="4" data-property="dateCreated">
                    <span className="updates-table-heading">
                      {i18n.t("update.dateColHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <DateFilter
                        field={{ name: 'Date Created', property: 'dateCreated' }}
                        firstFilterValue={this.getFilterValue('dateCreated', 'ge')}
                        secondFilterValue={this.getFilterValue('dateCreated', 'le')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                        showTime={false}
                      />
                    </span>
                  </th>
                  <th className={classNames("docUpdateDays", { "filter-applied": this.isFilterActive('docUpdateDays') })} scope="col" data-column-index="5" data-property="docUpdateDays">
                    <span className="updates-table-heading">
                      <span
                        data-tooltip-id="docUpdateDays-tooltip"
                        data-tooltip-content={i18n.t("update.docUpdateDaysTooltip")}
                      >
                        {i18n.t("update.docUpdateDaysHeading")}
                      </span>
                      <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <NumberFilter
                        field={{ name: 'Doc Update(days)', property: 'docUpdateDays' }}
                        firstFilterValue={this.getFilterValue('docUpdateDays', 'ge')}
                        secondFilterValue={this.getFilterValue('docUpdateDays', 'le')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                      />
                    </span>
                  </th>
                  <th className={classNames("panvivaId", { "filter-applied": this.isFilterActive('panvivaDocumentId') })} scope="col" data-column-index="6" data-property="documentId">
                    <span className="updates-table-heading">
                      {i18n.t("update.panvivaIdHeading")} <span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                      <NumberFilter
                        field={{ name: 'Panviva ID', property: 'panvivaDocumentId' }}
                        firstFilterValue={this.getFilterValue('panvivaDocumentId', 'ge')}
                        secondFilterValue={this.getFilterValue('panvivaDocumentId', 'le')}
                        onFilterApply={this.handleFilterApply}
                        onClear={this.handleFilterClear}
                        className="panviva-id-filter"
                      />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayResults && displayResults.length > 0 ? (
                  displayResults.map(item => renderListItem(item))
                ) : !isSkeleton ? (
                  <tr>
                    <td colSpan={7} className="text-center">No results found</td>
                  </tr>
                ) : (
                  [...Array(numberOfEmptyRows)].map((item, i) => <SkeletonTableRow numOfColumns={7} key={i} />)
                )}
              </tbody>
            </table>
          </div>
          { isSkeleton && <EmptyListModal message={i18n.t("update.upToDateModal.bodyMessage")} title={i18n.t("update.upToDateModal.headingMessage")} /> }
        </div>
        <Tooltip
          id="docUpdateDays-tooltip"
          place="bottom"
          variant="info"
          className="cc-tooltip"
        />
      </div>
    )
  }

  // hasActiveFilters method removed as it's no longer used

  public componentDidMount() {
    this.initSortingState();
    
    // Set default sorting by Doc Update(days) in descending order if no sorting is specified
    if (!this.props.orderBy && this.props.onSortChange) {
      this.props.onSortChange('docUpdateDays desc');
    }
  }

  private initSortingState() {
    if (this.props.orderBy) {
      const table = document.getElementById('updates-table');
      if (table) {
        const [property, direction] = this.props.orderBy.split(' ');
        const columnHeaders = [].slice.call(table.querySelectorAll('th'));
        
        for (let i = 0; i < columnHeaders.length; i++) {
          const ch = columnHeaders[i];
          const dataProperty = ch.getAttribute('data-property');
          
          if (dataProperty === property) {
            ch.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending');
            break;
          }
        }
      }
    }
  }

  private handleClickHeader(event: React.MouseEvent) {
    const target = event.currentTarget.closest('th');
    if (target && this.props.onSortChange) {
      const property = target.getAttribute('data-property');
      const currentSort = target.getAttribute('aria-sort');
      
      // Remove sort indicators from all other columns
      const table = document.getElementById('updates-table');
      if (table) {
        const columnHeaders = [].slice.call(table.querySelectorAll('th'));
        for (let i = 0; i < columnHeaders.length; i++) {
          if (columnHeaders[i] !== target && columnHeaders[i].hasAttribute('aria-sort')) {
            columnHeaders[i].removeAttribute('aria-sort');
          }
        }
      }
      
      // Toggle sort direction
      let newDirection = 'desc';
      if (currentSort === 'descending') {
        target.setAttribute('aria-sort', 'ascending');
        newDirection = 'asc';
      } else {
        target.setAttribute('aria-sort', 'descending');
        newDirection = 'desc';
      }
      
      this.props.onSortChange(`${property} ${newDirection}`);
    }
  }
}

const renderListItem = (item: Api.INotificationViewModel) => (
  <UpdateListItem update={item} key={item.id} />
);

const mapStateToProps = (state: IStoreState) => ({
  filters: state.updates.filters,
  filteredResults: state.updates.filteredResults
});

const mapDispatchToProps = {
  setFilter,
  clearFilter,
  clearAllFilters
};

export default connect(mapStateToProps, mapDispatchToProps)(UpdateList);
export { UpdateList };
